import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectLoggedInStatus } from '@redux/membership/selectors';
import { tkHost } from '@settings';
import { AppSettings } from '@tkww/orion-web-app-kernel';
import { DefaultPageTemplate } from '@tkww/orion-web-ds-page-layout';
import {
	GlobalFooterPagelet,
	GlobalHeaderPagelet,
} from '@tkww/orion-web-global-pagelets';
import { logOut } from '@xo-union/store-membership-redux';
import MembershipModalRedux, {
	type OpenModalType,
} from '@xo-union/tk-component-membership-modal-redux';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { analyticsProps, metaData } from '../../utils/membershipHelpers';

type ActiveProduct =
	| 'planning-tools'
	| 'vendors'
	| 'wedding-website'
	| 'guests-rsvps'
	| 'invitations'
	| 'registry'
	| 'attire-rings'
	| 'ideas-advice';

interface GlobalTemplateProps {
	children: React.ReactNode;
	activeProduct?: ActiveProduct;
	isMobileApp?: boolean;
}

export function GlobalTemplate({
	children,
	activeProduct = 'vendors',
	isMobileApp = false,
}: GlobalTemplateProps) {
	const membership = useSelector((state: Redux.State) => state.membership);
	const [openModal, setOpenModal] = useState<OpenModalType | undefined>();
	const dispatch = useAppDispatch();

	const isLoggedIn = useAppSelector(selectLoggedInStatus);

	const onClickSignUp = useCallback(() => {
		setOpenModal('SIGN_UP');
	}, []);

	const onClickLogIn = useCallback(() => {
		setOpenModal('LOG_IN');
	}, []);

	const onClickLogOut = () => {
		dispatch(logOut());
	};

	const closeModal = useCallback(() => {
		setOpenModal(undefined);
	}, []);

	if (isMobileApp) {
		return <>{children}</>;
	}

	return (
		<AppSettings settings={{ brandDist: 'theknot' }}>
			<DefaultPageTemplate
				header={
					<GlobalHeaderPagelet
						forwardProps={{
							isLoggedIn,
							analyticsProps,
							activeProduct,
							metaData,
							membership: { data: { member: membership.member } },
							favorites: {
								isFilled: false,
								href: `${tkHost}/wedding-vendor-manager`,
							},
							onClickSignUp,
							onClickLogIn,
							onClickLogOut,
						}}
					/>
				}
				footer={<GlobalFooterPagelet />}
			>
				{children}
			</DefaultPageTemplate>
			{openModal && (
				<MembershipModalRedux
					metadata={{
						userAction: 'header nav',
						applicationName: 'marketplace',
					}}
					openModalType={openModal}
					onClickLogIn={() => setOpenModal('LOG_IN')}
					onClickSignUp={() => setOpenModal('SIGN_UP')}
					onSignUpSuccess={closeModal}
					onLogInSuccess={closeModal}
					onClose={closeModal}
				/>
			)}
		</AppSettings>
	);
}
